import { definedMap, dontAwait } from "@glideapps/ts-necessities";
import { asString, isValueNotEmpty } from "common/components/component-helpers";
import { getLocalizedString } from "common/components/localization";
import type {
    ActionResult,
    Context,
    PotentialValue,
    RenderEnvironment,
    UploadProgressHandler,
    UploadSession,
    ValueProducers,
} from "common/components/types";
import { ActionSource, isUploadFileResponseError, LOADING } from "common/components/types";
import type { GlideFunctionalRenderer } from "common/components/uis";
import { useForce } from "hooks/use-force";
import { useNetworkStatus } from "hooks/use-network-status";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import { useForceValueProducers } from "hooks/use-value-producers";
import * as React from "react";
import type { TwotoneIcons } from "sharedUI/ui/views/glide-icon/glide-icon";
import { showUploadFailedRetryModal } from "webapp/lib/in-app-modals";
import FilePicker from "../ui/smart/file-picker/file-picker";

export type FilePickerWithUploadStateProps = Readonly<{
    ctx: Context;
    env: RenderEnvironment;
    valuePV: PotentialValue;
    onChangeValue?: () => Promise<ActionResult | undefined>;
    valueProducer: ValueProducers[0];
    validFlag: ValueProducers[0];
    isRequired: boolean;
    isEnabled: boolean;
    showPreview: boolean;
    caption: string;
    iconSource: TwotoneIcons;
    deleteModalTitle: string;
    capture?: string;
    mimePattern?: string;
    valueLabel?: string;
}>;

export class FilePickerWithUploadState extends React.PureComponent<FilePickerWithUploadStateProps> {
    private lastValue: string | undefined;
    private isCancelled: boolean = false;
    private uploadSession: UploadSession | undefined;

    private get value(): string {
        const { valuePV } = this.props;
        return definedMap(valuePV, asString) ?? "";
    }

    private setValid(isValid: boolean) {
        const { validFlag } = this.props;
        if (validFlag === undefined) return;
        validFlag.set(isValid);
    }

    private onNewURL(newURL: string | undefined) {
        const { valueProducer, isRequired } = this.props;
        if (newURL !== undefined) {
            dontAwait(this.onChange(newURL));
        }

        let isValid = true;
        if (valueProducer !== undefined && isRequired) {
            isValid = isValueNotEmpty(valueProducer.current) === true;
        }
        this.setValid(isValid);
        return newURL !== undefined;
    }

    private onChange = async (newValue: string) => {
        const { valueProducer, onChangeValue } = this.props;
        if (valueProducer === undefined) return;

        valueProducer.set(newValue);
        if (onChangeValue !== undefined) {
            await onChangeValue();
        }
    };

    private onDropFile = async (file: File, progressHandler: UploadProgressHandler) => {
        const { env } = this.props;
        this.setValid(false);
        this.lastValue = this.value;
        this.isCancelled = false;
        if (this.uploadSession !== undefined) {
            this.uploadSession.cancel();
        }
        const uploadSession = env.appEnvironment.uploadAppFile(file, "file-picker", progressHandler);
        this.uploadSession = uploadSession;

        const clearUploadSession = () => {
            if (this.uploadSession === uploadSession) {
                this.uploadSession = undefined;
            }
        };

        while (true) {
            const response = await uploadSession.attempt();
            if (this.isCancelled) return false;
            if (!isUploadFileResponseError(response)) {
                clearUploadSession();
                return this.onNewURL(response.path);
            }
            if (!(await showUploadFailedRetryModal(response, env.appEnvironment.appKind))) {
                clearUploadSession();
                return false;
            }
        }
    };

    private onCancel = (cancelled: boolean) => {
        if (cancelled && !this.isCancelled) {
            this.isCancelled = true;
            if (this.uploadSession !== undefined) {
                this.uploadSession.cancel();
                this.uploadSession = undefined;
            }
            this.onNewURL(this.lastValue);
        }
    };

    public render() {
        const {
            isRequired,
            isEnabled,
            caption,
            capture,
            mimePattern,
            iconSource,
            deleteModalTitle,
            showPreview,
            valueLabel,
            env: {
                appEnvironment: { appKind },
            },
        } = this.props;
        return (
            <FilePicker
                value={this.value}
                valueLabel={valueLabel}
                isEnabled={isEnabled}
                onChange={this.onChange}
                onDropFile={this.onDropFile}
                title={caption}
                isRequired={isRequired}
                capture={capture}
                mimePattern={mimePattern}
                iconSource={iconSource}
                showPreview={showPreview}
                deleteModalTitle={deleteModalTitle}
                deleteModalDescription={getLocalizedString("actionCannotBeUndone", appKind)}
                onCancel={this.onCancel}
                appKind={appKind}
            />
        );
    }
}
export const FilePickerMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [[valuePV, onChangeValue, isEnabledValue, , caption, isRequiredValue], [valueProducer, , , validFlag]] =
        useForceValueProducers(getValueProducers, force, ctx);
    const { onClick: onClickChange } = useOnClickFromAction(onChangeValue, ActionSource.FilePicker, ctx, env);
    const onLine = useNetworkStatus();

    if (valuePV === LOADING || caption === LOADING) {
        return env.renderProgress();
    }

    const isEnabled = onLine && (onClickChange === undefined || isEnabledValue !== false);
    const isRequired = isRequiredValue === true;
    const onChangeValueCB = onClickChange !== undefined ? () => onClickChange(undefined) : undefined;

    let value = "";
    if (valuePV !== undefined) {
        value = asString(valuePV);
    }

    if (isRequired && isValueNotEmpty(value) !== true && validFlag !== undefined) {
        validFlag.set(false);
    }

    let captionString = "";
    if (caption !== undefined) {
        captionString = asString(caption);
    }

    return (
        <FilePickerWithUploadState
            ctx={ctx}
            env={env}
            valuePV={valuePV}
            onChangeValue={onChangeValueCB}
            valueProducer={valueProducer}
            validFlag={validFlag}
            isRequired={isRequired}
            isEnabled={isEnabled}
            showPreview={false}
            deleteModalTitle={getLocalizedString("areYouSureYouWantToDeleteThisFile", env.appEnvironment.appKind)}
            caption={captionString}
            valueLabel={getLocalizedString("chooseAFile", env.appEnvironment.appKind)}
            iconSource={"tt-file-upload"}
        />
    );
};
